import React, { useState, useEffect } from 'react';
import ApiService from '../../services/ApiService';

const ALL_MODULES = [
    { key: 'dashboard', label: 'Dashboard', icon: 'fa-home' },
    { key: 'patients', label: 'Patients', icon: 'fa-user-injured' },
    { key: 'users', label: 'Users', icon: 'fa-users' },
    { key: 'specialist_visits', label: 'Specialist Visits', icon: 'fa-user-md' },
    { key: 'appointments', label: 'Appointments', icon: 'fa-calendar-check' },
    { key: 'appointment_reminders', label: 'Appointment Reminders', icon: 'fa-bell' },
    { key: 'products', label: 'Products & Inventory', icon: 'fa-box' },
    { key: 'orders', label: 'Sales & Orders', icon: 'fa-shopping-cart' },
    { key: 'purchases', label: 'Purchases', icon: 'fa-file-invoice-dollar' },
    { key: 'expenses', label: 'Expenses', icon: 'fa-money-bill-wave' },
    { key: 'data_management', label: 'Data Management', icon: 'fa-database' },
    { key: 'reports', label: 'Reports', icon: 'fa-chart-bar' },
];

const ROLES = [
    { key: 'doctor', label: 'Doctors' },
    { key: 'receptionist', label: 'Receptionists' },
    { key: 'specialist', label: 'Specialists' },
];

const PermissionsPage = () => {
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const res = await ApiService.request({ method: 'GET', url: '/admin/role-permissions' });
            setPermissions(res.data);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const toggleModule = (role, module) => {
        if (module === 'dashboard') return; // dashboard always required
        setPermissions(prev => {
            const current = prev[role] || [];
            const updated = current.includes(module)
                ? current.filter(m => m !== module)
                : [...current, module];
            return { ...prev, [role]: updated };
        });
    };

    const saveRole = async (role) => {
        setSaving(role);
        setMessage(null);
        try {
            await ApiService.request({
                method: 'PUT',
                url: `/admin/role-permissions/${role}`,
                data: { modules: permissions[role] || [] },
            });
            setMessage({ type: 'success', text: `Permissions for ${role} updated successfully.` });
        } catch (e) {
            setMessage({ type: 'danger', text: 'Failed to update permissions.' });
        }
        setSaving(null);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Role Permissions
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">

                    {message && (
                        <div className={`alert alert-${message.type} alert-dismissible mb-5`} role="alert">
                            {message.text}
                            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                        </div>
                    )}

                    {loading ? (
                        <div className="d-flex justify-content-center p-5">
                            <div className="spinner-border text-base"></div>
                        </div>
                    ) : (
                        <div className="row g-4">
                            {ROLES.map(role => (
                                <div key={role.key} className="col-md-4">
                                    <div className="card shadow-sm h-100">
                                        <div className="card-header bg-base2 text-white">
                                            <h5 className="py-5 fw-bold">{role.label}</h5>
                                        </div>
                                        <div className="card-body">
                                            {ALL_MODULES.map(mod => {
                                                const isChecked = (permissions[role.key] || []).includes(mod.key);
                                                const isDisabled = mod.key === 'dashboard';
                                                return (
                                                    <div key={mod.key} className="form-check mb-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`${role.key}-${mod.key}`}
                                                            checked={isChecked}
                                                            disabled={isDisabled}
                                                            onChange={() => toggleModule(role.key, mod.key)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`${role.key}-${mod.key}`}>
                                                            <i className={`fas ${mod.icon} me-2 text-muted`}></i>
                                                            {mod.label}
                                                            {isDisabled && <span className="text-muted ms-1 small">(always on)</span>}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="card-footer">
                                            <button
                                                className="btn btn-base w-100"
                                                onClick={() => saveRole(role.key)}
                                                disabled={saving === role.key}
                                            >
                                                {saving === role.key ? (
                                                    <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                                                ) : 'Save Permissions'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default PermissionsPage;
